import { apiClient } from './apiClient';
import type { SalesPoint, SalesPointFormData } from '../../types';

export interface SalesPointListQueryParams {
  readonly limit?: number;
  readonly offset?: number;
  readonly q?: string;
  readonly sort?: string;
  readonly dir?: 'asc' | 'desc';
  readonly txId?: string;
}

function buildSalesPointListQuery(params?: SalesPointListQueryParams): string {
  if (params === undefined) {
    return '';
  }
  const search = new URLSearchParams();
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    search.set('offset', String(params.offset));
  }
  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.sort !== undefined) {
    search.set('sort', params.sort);
  }
  if (params.dir !== undefined) {
    search.set('dir', params.dir);
  }
  if (params.txId !== undefined && params.txId.length > 0) {
    search.set('txId', params.txId);
  }
  return search.toString();
}

/** Admin sales-point CRUD (`/api/v1/admin/sales-points`). */
export const salesPointsApi = {
  getAll: () => apiClient.get<{ salesPoints: SalesPoint[] }>('/api/v1/admin/sales-points'),

  list: (params?: SalesPointListQueryParams | string) => {
    const query =
      typeof params === 'string' ? params : buildSalesPointListQuery(params);
    const path =
      query.length > 0
        ? `/api/v1/admin/sales-points?${query}`
        : '/api/v1/admin/sales-points';
    return apiClient.get<{ salesPoints: SalesPoint[] }>(path);
  },
  getForInventory: () =>
    apiClient.get<{ salesPoints: SalesPoint[] }>('/api/v1/admin/sales-points/inventory'),

  getById: (id: number) =>
    apiClient.get<{ salesPoint: SalesPoint }>(`/api/v1/admin/sales-points/${id}`),

  create: (data: SalesPointFormData) =>
    apiClient.post<{ salesPoint: SalesPoint }>('/api/v1/admin/sales-points', data),

  update: (id: number, data: Partial<SalesPointFormData>) =>
    apiClient.put<{ salesPoint: SalesPoint }>(`/api/v1/admin/sales-points/${id}`, data),

  deactivate: (id: number) =>
    apiClient.post<{ salesPoint: SalesPoint }>(`/api/v1/admin/sales-points/${id}/deactivate`, {}),

  reactivate: (id: number) =>
    apiClient.post<{ salesPoint: SalesPoint }>(`/api/v1/admin/sales-points/${id}/reactivate`, {}),

  permanentDelete: (id: number) =>
    apiClient.delete<Record<string, never>>(`/api/v1/admin/sales-points/${id}/permanent-delete`),

  setPickupPin: (id: number, pin: string) =>
    apiClient.post<{ success: boolean }>(`/api/v1/admin/sales-points/${id}/pickup-pin`, { pin }),

  setDefaultVatRate: (id: number, defaultVatRate: number | null) =>
    apiClient.put<{ salesPoint: SalesPoint }>(`/api/v1/admin/sales-points/${id}/default-vat-rate`, {
      defaultVatRate,
    }),

  getProviderSlots: (id: number) =>
    apiClient.get<{
      slots: Array<{
        providerKind: 'email' | 'receipt' | 'sms';
        providerSlotId: string;
        isEnabled: boolean;
      }>;
      commsOverride: {
        email: boolean;
        receipt: boolean;
        sms: boolean;
      };
    }>(`/api/v1/admin/sales-points/${id}/provider-slots`),

  putProviderSlots: (
    id: number,
    slots: Array<{
      providerKind: 'email' | 'receipt' | 'sms';
      providerSlotId: string;
      isEnabled: boolean;
    }>,
  ) =>
    apiClient.put<{ updated: boolean }>(`/api/v1/admin/sales-points/${id}/provider-slots`, {
      slots,
    }),

  /** Dedicated cash-box Ověřeno — sets commerceConfigJson.cashBoxVerifiedAt + ByAdminId. */
  confirmCashBoxVerified: (id: number) =>
    apiClient.post<{
      salesPointId: number;
      cashBoxVerifiedAt: string;
      cashBoxVerifiedByAdminId: number;
    }>(`/api/v1/admin/sales-points/${id}/cash-box/confirm`, {}),

  /** Intentional cash-box Ověřeno invalidate. */
  clearCashBoxVerified: (id: number) =>
    apiClient.post<{ salesPointId: number; cleared: true }>(
      `/api/v1/admin/sales-points/${id}/cash-box/clear`,
      {},
    ),
};
